import moment from 'moment'
import { pick } from 'lodash'
import { CanceledError } from 'axios'
import { FiltradorDeErros } from 'src/erros'
import { formatarDataParaEnvio, obterDataValida } from 'src/utils/formatadoras/formatadoras'
import { multiSelect } from 'src/common/selectors'
import { API, retryRequest } from 'src/services'
// import { empresaUsaFuncionalidadeVerificarDia } from './utils'
import { hasPermission } from 'src/utils'

function ehRequisicaoCancelada (erro) {
  return erro instanceof CanceledError || erro?.code === 'ERR_CANCELED'
}

function defaultState () {
  return {
    ...multiSelect.mapStates(['companies', 'employees', 'userGroups']),
    request: {
      total: null,
      verify: null
    },
    startDate: moment().startOf('month').toDate(),
    endDate: moment().endOf('month').toDate(),
    verifyMode: false,
    verificationKind: 'oddPts',
    verificationFilters: {},
    erroNaBusca: false,
    options: {
      tmsQuery: 'gte',
      tmsIgnoreZeros: true,
      descontoBancoDeHoras: false,
      ehRubrica: false,
      ocultarDiasVerificados: false
    },
    pagination: {
      page: 0,
      total: null,
      perPage: null,
      pages: null
    },
    totals: {
      oddPts: 0,
      pendency: 0
    }
  }
}

const mutations = {
  ...multiSelect.mapMutations(['companies', 'employees', 'userGroups']),
  startDate: (state, payload) => { state.startDate = obterDataValida(payload) },
  endDate: (state, payload) => { state.endDate = obterDataValida(payload) },
  datePeriod: (state, payload) => {
    state.startDate = obterDataValida(payload.startDate)
    state.endDate = obterDataValida(payload.endDate)
  },
  erroNaBusca(state, payload) {
    state.erroNaBusca = payload
  },
  request (state, { type, request }) {
    state.request = {
      ...state.request,
      [type]: request
    }
  },
  verificationKind (state, payload) {
    state.verificationKind = payload
  },
  verificationFilters (state, payload) {
    state.verificationFilters = payload
  },
  pagination (state, payload) {
    state.pagination = { ...state.pagination, ...payload }
  },
  resetPagination (state) {
    state.pagination = defaultState().pagination
  },
  totals (state, payload) {
    state.totals = payload
  },
  options (state, payload) {
    state.options = {...state.options, ...payload}
  }
}

const actions = {
  async verifyTimesheet ({ rootState, state, commit, dispatch }, kind) {
    const toLoad = {
      startDate: formatarDataParaEnvio(state.startDate),
      endDate: formatarDataParaEnvio(state.endDate),
      verifyKind: state.verificationKind,
      filters: Array.isArray(state.verificationFilters)
        ? state.verificationFilters.map(filtro => pick(filtro, 'field', 'value'))
        : state.verificationFilters,
      page: state.pagination.page,
      options: state.options
    }

    if (kind === 'work-place') {
      toLoad.companies = state.companies.selected.map(i => i.id)
    } else if (kind === 'user-group') {
      toLoad.userGroups = state.userGroups.selected.map(i => i.id)
    } else if (kind === 'employee') {
      toLoad.employees = state.employees.selected.map(i => i.id)
    }

    toLoad.ptsByCompany = rootState.sheet.toolBox.configs.ptsByCompany && kind === 'work-place'
    
    const empresaUsaFuncionalidadeVerificarDia = rootState.modulosEFuncionalidades?.funcionalidades?.acesso_funcionalidade_verificar_dia?.enabled || false
    
    if (empresaUsaFuncionalidadeVerificarDia && hasPermission({module: 'verificardia', action: 'post'})) {
      toLoad.options.ocultarDiasVerificados = state.options.ocultarDiasVerificados
    }

    commit('erroNaBusca', false)
    commit('sheet/loading', { lines: true }, { root: true })

    try {
      const result = await retryRequest(API.timesheetVerify.save, {}, {
        ...toLoad,
        before (request) {
          if (state.request.verify) {
            state.request.verify.abort()
          }
          commit('request', { type: 'verify', request })
        }
      })

      commit('pagination', result.data.success.pagination)
      commit('sheet/state', result.data.success, { root: true })

      const onlyPopulateRealLines = state.verificationKind !== 'events' ||
        !state.verificationFilters.find(i => i.field === 'falta')

      dispatch(
        'sheet/populateLines',
        { startDate: state.startDate, endDate: state.endDate, onlyPopulateRealLines },
        { root: true }
      )
    } catch (erro) {
      if (ehRequisicaoCancelada(erro)) {
        return
      }

      FiltradorDeErros.capturarErro(erro)
      commit('erroNaBusca', true)
    } finally {
      commit('sheet/loading', { lines: false }, { root: true })
      commit('request', { type: 'verify', request: null })
    }
  },
  async getTotal ({ state, rootState, commit }, kind) {
    const toLoad = {
      startDate: formatarDataParaEnvio(state.startDate),
      endDate: formatarDataParaEnvio(state.endDate),
      companies: [rootState.userInfo.firstCompany],
      onlyTotal: true
    }

    if (kind === 'work-place') {
      toLoad.companies = state.companies.selected.map(i => i.id)
    } else if (kind === 'user-group') {
      toLoad.userGroups = state.userGroups.selected.map(i => i.id)
    } else if (kind === 'employee') {
      toLoad.employees = state.employees.selected.map(i => i.id)
    } else {
      toLoad.companies = [rootState.userInfo.firstCompany]
    }

    try {
      const result = await API.timesheetVerify.save({
        ...toLoad,
        before(request) {
          if (state.request.total) {
            state.request.total.abort()
          }
          commit('request', { type: 'total', request })
        }
      })
      commit('totals', result.body.success?.totals || defaultState().totals)
    } catch (erro) {
      if (ehRequisicaoCancelada(erro)) {
        return
      }

      FiltradorDeErros.capturarErro(erro)
    } finally {
      commit('request', { type: 'total', request: null })
    }
  }
}

export default {
  namespaced: true,
  state: defaultState(),
  actions,
  mutations
}
